import {
  LOG_API_CALL_DURATION_ENABLE,
  PEOPLE_SEARCH_API_PREFIX,
} from '@aibees/elephant/constants';
import {
  BULK_FILTER_VALUE_SEPARATOR,
  COMPANY_ID_FILTER_SEPARATOR,
  DURATION_FILTER_VALUE_SEPARATOR,
  FILTER_VALUE_SEPARATOR,
  RANGE_FILTER_SEPARATOR,
} from '@aibees/elephant/constants/filters';
import {
  useSearchQueryFilter,
  useSearchTypeFromRoute,
} from '@aibees/elephant/hooks';
import { useModalityChangeCounterStore } from '@aibees/elephant/store/modality-change-counter';
import {
  SearchMatchType,
  SearchMode,
  SearchType,
} from '@aibees/elephant/types';
import {
  BaseEntity,
  HttpMethod,
  PaginationRequest,
  PaginationResponse,
} from '@aibees/shared/definition';
import { useBaseQuery } from '@aibees/shared/http';
import {
  cleanUrl,
  objSanitizeNilValues,
  peformanceLogTime,
  stableHash,
} from '@aibees/shared/utils';
import { useWorkspace } from '@aibees/shared/workspace';
import { UseQueryOptions } from '@tanstack/react-query';
import { isEmpty, omit } from 'lodash-es';
import { useDeferredValue, useMemo } from 'react';
import { mapJobRoleTreeOptionForBackend } from '../../search-filters/config/job-functions';
import {
  QueryTarget,
  SearchModality,
} from '../../search-table-company/hooks/types';
import {
  mappingCustomRange,
  mappingFollowerRanges,
} from '../../search-table-company/range-mappers';
import {
  addCompanyInclusionQuery,
  addEducationInclusionQuery,
  addInclusionQuery,
  addInclusionWithSourceQuery,
  addJobTitleQuery,
  addSearchListInclusionQuery,
  setNestedRange,
  transform as companyRequestTransform,
} from '../../search-table-company/hooks/use-browse-company-search'; // TODO this must be in separate file not related company folder
import { PEOPLE_SEARCH_API_RESPONSE_PERF_MARK } from '../config';
import { PeopleTableInfo } from '../types';
import { useUpdatePersonSearchItemsStore } from './use-update-person-search-items-store';

export type BrowsePeopleSearchRequest = PaginationRequest<{
  data?: {
    type?: string;
    value?: string;
  } & Record<string, any>;
  value?: string[];
}>;

export type PeopleSearchResponse = PaginationResponse<PeopleTableInfo>;

/**
 * Explicit dependencies for the core hook, allowing callers to provide
 * pre-resolved values instead of subscribing to stores internally.
 */
export interface BrowsePeopleSearchDeps {
  workspace?: string;
  type?: SearchType;
  searchParams: Record<string, any>;
  modalityVersion: Record<string, string>;
}

/**
 * Core hook that performs the people search query.
 * Does NOT subscribe to any stores — all dependencies come through `deps`.
 */
export function useBrowsePeopleSearchCore<T extends BaseEntity>(
  request: BrowsePeopleSearchRequest,
  deps: BrowsePeopleSearchDeps,
  options?: UseQueryOptions<
    PaginationResponse<PeopleTableInfo>,
    any,
    PaginationResponse<T>
  > & { forceEnabled?: boolean },
  isNotSearchTableQuery?: boolean
) {
  const { workspace, type, searchParams, modalityVersion } = deps;

  const { page, size, ...requestData } = {
    ...request,
    page: request.page || 0,
    size: request.size || 25,
  };

  // Sanitize and merge parameters
  const reqParams = objSanitizeNilValues(
    omit({ ...requestData.data, ...searchParams }, 'type', 'page', 'size')
  );

  // Transform the parameters
  const transformedRequestParams = transform(reqParams);

  const modalityVersionQueryKey = useMemo(() => {
    return modalityVersion[
      (transformedRequestParams.modality ??
        SearchModality.TOTAL) as SearchModality
    ];
  }, [transformedRequestParams, modalityVersion]);

  // Hash query key parts for stability
  const queryKey = [
    'useBrowsePeopleSearch',
    type,
    page,
    size,
    stableHash(omit(transformedRequestParams, 'modality')),
    modalityVersionQueryKey, // This will cause refetch on each modality change
  ];

  const isRequestEmpty = useMemo(() => {
    return isEmpty(reqParams);
  }, [reqParams]);

  const query = useBaseQuery<
    Omit<BrowsePeopleSearchRequest, 'sort'> & { sort?: [string] },
    PaginationResponse<PeopleTableInfo>,
    PaginationResponse<T>
  >(
    queryKey,
    {
      url: PEOPLE_SEARCH_API_PREFIX.replace(':wid', String(workspace)),
      method: HttpMethod.post,
      headers: { 'x-customer-id': workspace },
      data: {
        page,
        size,
        ...{
          ...transformedRequestParams,
          value: Array.isArray(transformedRequestParams.value)
            ? transformedRequestParams.value
            : transformedRequestParams.value
            ? [transformedRequestParams.value]
            : undefined,
        },
      },
    },
    {
      ...{
        ...options,
        ...{
          enabled: options?.enabled && !isRequestEmpty,
          // keepPreviousData: true,
          logTime: true,
        },
        select: options?.select as (
          data: PaginationResponse<PeopleTableInfo>
        ) => PaginationResponse<T>,
      },
      onSuccess(data) {
        performance.mark(PEOPLE_SEARCH_API_RESPONSE_PERF_MARK);
        if (LOG_API_CALL_DURATION_ENABLE) {
          peformanceLogTime('useBrowsePeopleSearch onSuccess');
        }
      },
    }
  );

  useUpdatePersonSearchItemsStore(query, isNotSearchTableQuery);

  return query;
}

/**
 * Convenience wrapper that reads dependencies from stores/context.
 * For the advance-table path, prefer `useBrowsePeopleSearchCore` directly.
 */
export function useBrowsePeopleSearch<T extends BaseEntity>(
  request: BrowsePeopleSearchRequest,
  options?: UseQueryOptions<
    PaginationResponse<PeopleTableInfo>,
    any,
    PaginationResponse<T>
  > & { forceEnabled?: boolean },
  isNotSearchTableQuery?: boolean
) {
  const workspace = useWorkspace()?.id;
  const type = useSearchTypeFromRoute();
  const { params } = useSearchQueryFilter();
  const deferredParams = useDeferredValue(params);
  const { counter: modalityVersion } = useModalityChangeCounterStore();

  return useBrowsePeopleSearchCore<T>(
    request,
    { workspace, type, searchParams: deferredParams, modalityVersion },
    options,
    isNotSearchTableQuery
  );
}

export function transform(r: Record<string, any>) {
  let req = companyRequestTransform(r, SearchMode.Similarity);
  const latestActiveCompanyOnly = !(
    !!r.latest_active_company_only && r.latest_active_company_only === 'false'
  );

  const currentCompanyFilterType = latestActiveCompanyOnly
    ? 'latest'
    : 'current';

  const latestJobTitleFilterType =
    !!r.latest_active_job_title_only &&
    r.latest_active_job_title_only === 'false'
      ? 'current'
      : 'latest';

  if (r.include_education_school) {
    const cleanValue = r.include_education_school
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => i.split('::')[1])
      .join(FILTER_VALUE_SEPARATOR);

    req = addEducationInclusionQuery(
      req,
      'education',
      'any',
      'include',
      cleanValue,
      QueryTarget.contact,
      'school'
    );
  }

  if (r.include_all_of_education_school) {
    const cleanValue = r.include_all_of_education_school
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => i.split('::')[1])
      .join(FILTER_VALUE_SEPARATOR);

    req = addEducationInclusionQuery(
      req,
      'education',
      'all',
      'include',
      cleanValue,
      QueryTarget.contact,
      'school'
    );
  }

  if (r.exclude_education_school) {
    const cleanValue = r.exclude_education_school
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => i.split('::')[1])
      .join(FILTER_VALUE_SEPARATOR);

    req = addEducationInclusionQuery(
      req,
      'education',
      'any',
      'exclude',
      cleanValue,
      QueryTarget.contact,
      'school'
    );
  }

  if (r.include_education_degree) {
    const cleanValue = r.include_education_degree
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => i.split('::')[0])
      .join(FILTER_VALUE_SEPARATOR);

    req = addEducationInclusionQuery(
      req,
      'education',
      'any',
      'include',
      cleanValue,
      QueryTarget.contact,
      'degree',
      'include_education_degree',
      r.exact_match
    );
  }

  if (r.include_all_of_education_degree) {
    const cleanValue = r.include_all_of_education_degree
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => i.split('::')[0])
      .join(FILTER_VALUE_SEPARATOR);

    req = addEducationInclusionQuery(
      req,
      'education',
      'all',
      'include',
      cleanValue,
      QueryTarget.contact,
      'degree',
      'include_all_of_education_degree',
      r.exact_match
    );
  }

  if (r.exclude_education_degree) {
    const cleanValue = r.exclude_education_degree
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => i.split('::')[0])
      .join(FILTER_VALUE_SEPARATOR);

    req = addEducationInclusionQuery(
      req,
      'education',
      'any',
      'exclude',
      cleanValue,
      QueryTarget.contact,
      'degree',
      'exclude_education_degree',
      r.exact_match
    );
  }

  if (r.include_education_major) {
    const cleanValue = r.include_education_major
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => i.split('::')[0])
      .join(FILTER_VALUE_SEPARATOR);

    req = addEducationInclusionQuery(
      req,
      'education',
      'any',
      'include',
      cleanValue,
      QueryTarget.contact,
      'fieldOfStudy',
      'include_education_major',
      r.exact_match
    );
  }

  if (r.include_all_of_education_major) {
    const cleanValue = r.include_all_of_education_major
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => i.split('::')[0])
      .join(FILTER_VALUE_SEPARATOR);

    req = addEducationInclusionQuery(
      req,
      'education',
      'all',
      'include',
      cleanValue,
      QueryTarget.contact,
      'fieldOfStudy',
      'include_all_of_education_major',
      r.exact_match
    );
  }

  if (r.exclude_education_major) {
    const cleanValue = r.exclude_education_major
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => i.split('::')[0])
      .join(FILTER_VALUE_SEPARATOR);

    req = addEducationInclusionQuery(
      req,
      'education',
      'any',
      'exclude',
      cleanValue,
      QueryTarget.contact,
      'fieldOfStudy',
      'exclude_education_major',
      r.exact_match
    );
  }

  if (r.education_year) {
    const [start, end] = r.education_year.split(RANGE_FILTER_SEPARATOR);
    req[QueryTarget.contact].education = {
      ...req[QueryTarget.contact].education,
      ...{
        date: {
          ...req[QueryTarget.contact].education?.date,
          ...{
            start: start ?? undefined,
            end: end === 'Present' ? undefined : end,
            present: end === 'Present' ? true : undefined,
          },
        },
      },
    };
  }

  if (r.include_current_company) {
    const cleanValue = r.include_current_company
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => i.split('::')[1])
      .join(FILTER_VALUE_SEPARATOR);

    req = addCompanyInclusionQuery(
      req,
      currentCompanyFilterType,
      'any',
      'include',
      cleanValue
    );
  }

  if (r.exclude_current_company) {
    const cleanValue = r.exclude_current_company
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => i.split('::')[1])
      .join(FILTER_VALUE_SEPARATOR);

    req = addCompanyInclusionQuery(
      req,
      currentCompanyFilterType,
      'any',
      'exclude',
      cleanValue
    );
  }

  if (r.include_previous_company) {
    const cleanValue = r.include_previous_company
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => i.split('::')[1])
      .join(FILTER_VALUE_SEPARATOR);

    req = addCompanyInclusionQuery(
      req,
      'previous',
      'any',
      'include',
      cleanValue
    );
  }

  if (r.exclude_previous_company) {
    const cleanValue = r.exclude_previous_company
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => i.split('::')[1])
      .join(FILTER_VALUE_SEPARATOR);

    req = addCompanyInclusionQuery(
      req,
      'previous',
      'any',
      'exclude',
      cleanValue
    );
  }

  if (r.bulk_include_current_company) {
    const bulkCleanValue = r.bulk_include_current_company
      .split(FILTER_VALUE_SEPARATOR)
      .map((d: string) => cleanUrl(d))
      .join(FILTER_VALUE_SEPARATOR);

    req = addCompanyInclusionQuery(
      req,
      currentCompanyFilterType,
      'any',
      'include',
      bulkCleanValue
    );
  }

  if (r.bulk_exclude_current_company) {
    const bulkCleanValue = r.bulk_exclude_current_company
      .split(FILTER_VALUE_SEPARATOR)
      .map((d: string) => cleanUrl(d))
      .join(FILTER_VALUE_SEPARATOR);

    req = addCompanyInclusionQuery(
      req,
      currentCompanyFilterType,
      'any',
      'exclude',
      bulkCleanValue
    );
  }

  if (r.bulk_include_previous_company) {
    const bulkCleanValue = r.bulk_include_previous_company
      .split(FILTER_VALUE_SEPARATOR)
      .map((d: string) => cleanUrl(d))
      .join(FILTER_VALUE_SEPARATOR);

    req = addCompanyInclusionQuery(
      req,
      'previous',
      'any',
      'include',
      bulkCleanValue
    );
  }

  if (r.bulk_exclude_previous_company) {
    const bulkCleanValue = r.bulk_exclude_previous_company
      .split(FILTER_VALUE_SEPARATOR)
      .map((d: string) => cleanUrl(d))
      .join(FILTER_VALUE_SEPARATOR);

    req = addCompanyInclusionQuery(
      req,
      'previous',
      'any',
      'exclude',
      bulkCleanValue
    );
  }

  if (r.fullName) {
    req = addInclusionQuery(
      req,
      'fullName',
      'any',
      'include',
      r.fullName,
      QueryTarget.contact,
      'fullName',
      r.exact_match
    );
  }

  if (r.person_id)
    req = addInclusionQuery(
      req,
      'id',
      'any',
      'include',
      r.person_id,
      QueryTarget.contact
    );

  if (r.person_id_exclude)
    req = addInclusionQuery(
      req,
      'id',
      'any',
      'exclude',
      r.person_id_exclude,
      QueryTarget.contact
    );

  if (r.company_id)
    req = addInclusionQuery(
      req,
      'id',
      'any',
      'include',
      r.company_id,
      QueryTarget.account
    );

  if (r.include_contact_email)
    req = addInclusionQuery(
      req,
      'email',
      'any',
      'include',
      r.include_contact_email,
      QueryTarget.contact
    );

  if (r.current_job_role_seniority) {
    req = addInclusionQuery(
      req,
      'seniority',
      'any',
      'include',
      r.current_job_role_seniority,
      QueryTarget.contact
    );
  }

  if (r.current_job_include_job_title) {
    req[QueryTarget.contact] = {
      ...req[QueryTarget.contact],
      ...addJobTitleQuery(
        req[QueryTarget.contact],
        latestJobTitleFilterType,
        'any',
        'include',
        r.current_job_include_job_title,
        'current_job_include_job_title',
        r.exact_match
      ),
    };
  }

  if (r.current_job_exclude_job_title) {
    req[QueryTarget.contact] = {
      ...req[QueryTarget.contact],
      ...addJobTitleQuery(
        req[QueryTarget.contact],
        latestJobTitleFilterType,
        'any',
        'exclude',
        r.current_job_exclude_job_title,
        'current_job_exclude_job_title',
        r.exact_match
      ),
    };
  }

  if (r.current_job_all_of_job_title) {
    req[QueryTarget.contact] = {
      ...req[QueryTarget.contact],
      ...addJobTitleQuery(
        req[QueryTarget.contact],
        latestJobTitleFilterType,
        'all',
        'include',
        r.current_job_all_of_job_title,
        'current_job_all_of_job_title',
        r.exact_match
      ),
    };
  }

  if (r.prev_job_include_job_title) {
    req[QueryTarget.contact] = {
      ...req[QueryTarget.contact],
      ...addJobTitleQuery(
        req[QueryTarget.contact],
        'previous',
        'any',
        'include',
        r.prev_job_include_job_title,
        'prev_job_include_job_title',
        r.exact_match
      ),
    };
  }

  if (r.prev_job_exclude_job_title) {
    req[QueryTarget.contact] = {
      ...req[QueryTarget.contact],
      ...addJobTitleQuery(
        req[QueryTarget.contact],
        'previous',
        'any',
        'exclude',
        r.prev_job_exclude_job_title,
        'prev_job_exclude_job_title',
        r.exact_match
      ),
    };
  }

  if (r.prev_job_all_of_job_title) {
    req[QueryTarget.contact] = {
      ...req[QueryTarget.contact],
      ...addJobTitleQuery(
        req[QueryTarget.contact],
        'previous',
        'all',
        'include',
        r.prev_job_all_of_job_title,
        'prev_job_all_of_job_title',
        r.exact_match
      ),
    };
  }

  if (r.current_job_experience_current_job) {
    const seperatedMinMax = r.current_job_experience_current_job.split(
      RANGE_FILTER_SEPARATOR
    );
    const min = seperatedMinMax[0].split(DURATION_FILTER_VALUE_SEPARATOR);
    const max = seperatedMinMax[1].split(DURATION_FILTER_VALUE_SEPARATOR);

    req[QueryTarget.contact].experience = {
      ...req[QueryTarget.contact].experience,
      ...{
        current: {
          ...req[QueryTarget.contact].experience?.current,
          ...{
            duration: {
              ...req[QueryTarget.contact].experience?.current?.duration,
              ...{
                currentJob: {
                  min: {
                    year: min[0] ? Number(min[0]) : undefined,
                    month: min[1] ? Number(min[1]) : undefined,
                  },
                  max: {
                    year: max[0] ? Number(max[0]) : undefined,
                    month: max[1] ? Number(max[1]) : undefined,
                  },
                },
              },
            },
          },
        },
      },
    };
  }

  if (r.current_job_experience_current_company) {
    const seperatedMinMax = r.current_job_experience_current_company.split(
      RANGE_FILTER_SEPARATOR
    );
    const min = seperatedMinMax[0].split(DURATION_FILTER_VALUE_SEPARATOR);
    const max = seperatedMinMax[1].split(DURATION_FILTER_VALUE_SEPARATOR);

    req[QueryTarget.contact].experience = {
      ...req[QueryTarget.contact].experience,
      ...{
        current: {
          ...req[QueryTarget.contact].experience?.current,
          ...{
            duration: {
              ...req[QueryTarget.contact].experience?.current?.duration,
              ...{
                currentCompany: {
                  min: {
                    year: min[0] ? Number(min[0]) : undefined,
                    month: min[1] ? Number(min[1]) : undefined,
                  },
                  max: {
                    year: max[0] ? Number(max[0]) : undefined,
                    month: max[1] ? Number(max[1]) : undefined,
                  },
                },
              },
            },
          },
        },
      },
    };
  }

  if (r.current_job_experience_time) {
    const seperatedMinMax = r.current_job_experience_time.split(
      RANGE_FILTER_SEPARATOR
    );
    const min = seperatedMinMax[0].split(DURATION_FILTER_VALUE_SEPARATOR);
    const max = seperatedMinMax[1].split(DURATION_FILTER_VALUE_SEPARATOR);

    req[QueryTarget.contact].experience = {
      ...req[QueryTarget.contact].experience,
      ...{
        current: {
          ...req[QueryTarget.contact].experience?.current,
          ...{
            duration: {
              ...req[QueryTarget.contact].experience?.current?.duration,
              ...{
                total: {
                  min: {
                    year: min[0] ? Number(min[0]) : undefined,
                    month: min[1] ? Number(min[1]) : undefined,
                  },
                  max: {
                    year: max[0] ? Number(max[0]) : undefined,
                    month: max[1] ? Number(max[1]) : undefined,
                  },
                },
              },
            },
          },
        },
      },
    };
  }

  if (r.current_job_role_department) {
    const v = mapJobRoleTreeOptionForBackend(
      r.current_job_role_department.split(FILTER_VALUE_SEPARATOR)
    )
      .map((i: string) => (i.includes('::') ? i.split('::')[1] : i))
      .join(FILTER_VALUE_SEPARATOR);

    req = addInclusionQuery(
      req,
      'departmentAndFunction',
      'any',
      'include',
      v,
      QueryTarget.contact
    );
  }

  if (
    r.contact_include_zip_code_country &&
    r.contact_include_zip_code_distance
  ) {
    const geoLocation = r.contact_include_zip_code_country.split(':');
    req[QueryTarget.contact] = {
      ...req[QueryTarget.contact],
      ...{
        geoLocation: {
          position: { lat: geoLocation[1], lng: geoLocation[2] },
          radius: r.contact_include_zip_code_distance,
          unit: r.contact_include_zip_code_unit || 'Miles',
        },
      },
    };
  }

  if (r.include_languages_for_contact)
    req = addInclusionQuery(
      req,
      'language',
      'any',
      'include',
      r.include_languages_for_contact,
      QueryTarget.contact,
      'include_languages_for_contact',
      r.exact_match,
      SearchMatchType.WORD
    );

  if (r.exclude_languages_for_contact)
    req = addInclusionQuery(
      req,
      'language',
      'any',
      'exclude',
      r.exclude_languages_for_contact,
      QueryTarget.contact,
      'exclude_languages_for_contact',
      r.exact_match,
      SearchMatchType.WORD
    );

  if (r.all_of_languages_for_contact)
    req = addInclusionQuery(
      req,
      'language',
      'all',
      'include',
      r.all_of_languages_for_contact,
      QueryTarget.contact,
      'all_of_languages_for_contact',
      r.exact_match,
      SearchMatchType.WORD
    );

  if (r.number_of_languages_for_contact) {
    const tempValue = r.number_of_languages_for_contact.split(
      RANGE_FILTER_SEPARATOR
    );
    req[QueryTarget.contact].language = {
      ...req[QueryTarget.contact].language,
      ...{
        range: { start: tempValue[0], end: tempValue[1] },
      },
    };
  }

  if (r.include_certification)
    req = addInclusionQuery(
      req,
      'certification',
      'any',
      'include',
      r.include_certification,
      QueryTarget.contact,
      'include_certification',
      r.exact_match
    );

  if (r.exclude_certification)
    req = addInclusionQuery(
      req,
      'certification',
      'any',
      'exclude',
      r.exclude_certification,
      QueryTarget.contact,
      'exclude_certification',
      r.exact_match
    );

  if (r.all_of_certification)
    req = addInclusionQuery(
      req,
      'certification',
      'all',
      'include',
      r.all_of_certification,
      QueryTarget.contact,
      'all_of_certification',
      r.exact_match
    );

  if (r.include_skills)
    req = addInclusionQuery(
      req,
      'skill',
      'any',
      'include',
      r.include_skills,
      QueryTarget.contact,
      'include_skills',
      r.exact_match
    );

  if (r.exclude_skills)
    req = addInclusionQuery(
      req,
      'skill',
      'any',
      'exclude',
      r.exclude_skills,
      QueryTarget.contact,
      'exclude_skills',
      r.exact_match
    );

  if (r.all_of_skills)
    req = addInclusionQuery(
      req,
      'skill',
      'all',
      'include',
      r.all_of_skills,
      QueryTarget.contact,
      'all_of_skills',
      r.exact_match
    );

  if (r.profile_badge_contacts_include)
    req = addInclusionQuery(
      req,
      'profileBadge',
      'any',
      'include',
      r.profile_badge_contacts_include,
      QueryTarget.contact
    );

  if (r.profile_badge_contacts_exclude)
    req = addInclusionQuery(
      req,
      'profileBadge',
      'any',
      'exclude',
      r.profile_badge_contacts_exclude,
      QueryTarget.contact
    );

  if (r.profile_badge_contacts_all_of)
    req = addInclusionQuery(
      req,
      'profileBadge',
      'all',
      'include',
      r.profile_badge_contacts_all_of,
      QueryTarget.contact
    );

  if (r.person_linkedin_followers_predefined) {
    req = setNestedRange(req, QueryTarget.contact, 'linkedin', 'followers', {
      type: 'RANGE',
      range: mappingFollowerRanges(r.person_linkedin_followers_predefined),
    });
  }

  if (r.person_linkedin_followers_custom) {
    req = setNestedRange(req, QueryTarget.contact, 'linkedin', 'followers', {
      type: 'RANGE',
      range: mappingCustomRange(r.person_linkedin_followers_custom),
    });
  }

  if (r.person_linkedin_followers_availability) {
    req = setNestedRange(req, QueryTarget.contact, 'linkedin', 'followers', {
      type: 'NONE',
    });
  }

  if (r.person_linkedin_connections_predefined) {
    req = setNestedRange(req, QueryTarget.contact, 'linkedin', 'connections', {
      type: 'RANGE',
      range: mappingFollowerRanges(r.person_linkedin_connections_predefined),
    });
  }

  if (r.person_linkedin_connections_custom) {
    req = setNestedRange(req, QueryTarget.contact, 'linkedin', 'connections', {
      type: 'RANGE',
      range: mappingCustomRange(r.person_linkedin_connections_custom),
    });
  }

  if (r.person_linkedin_connections_availability) {
    req = setNestedRange(req, QueryTarget.contact, 'linkedin', 'connections', {
      type: 'NONE',
    });
  }

  if (r.maxPeople) {
    req[QueryTarget.contact] = {
      ...req[QueryTarget.contact],
      maxPeoplePerCompany: r.maxPeople,
    };
  }

  if (r.people_include_keywords) {
    req = addInclusionWithSourceQuery(
      req,
      'keyword',
      'any',
      'include',
      QueryTarget.contact,
      r.people_include_keywords,
      r.include_people_keywords_sources,
      r.include_people_keywords_sources_search_match
    );
  }

  if (r.people_exclude_keywords) {
    req = addInclusionWithSourceQuery(
      req,
      'keyword',
      'any',
      'exclude',
      QueryTarget.contact,
      r.people_exclude_keywords,
      r.people_exclude_keywords_sources,
      r.people_exclude_keywords_sources_search_match
    );
  }

  if (r.people_all_of_keywords) {
    req = addInclusionWithSourceQuery(
      req,
      'keyword',
      'all',
      'include',
      QueryTarget.contact,
      r.people_all_of_keywords,
      r.people_all_of_keywords_sources,
      r.people_all_of_keywords_sources_search_match
    );
  }

  if (r.contact_job_change === 'true') {
    req.contact = {
      ...req.contact,
      ...{ jobChange: true },
    };
  }

  if (r.people_search_list_id) {
    const v = r.people_search_list_id.split(COMPANY_ID_FILTER_SEPARATOR)[0];
    req = addSearchListInclusionQuery(
      req,
      'searchList',
      'any',
      'include',
      v,
      QueryTarget.contact,
      'personId'
    );
  }

  if (r.people_company_search_list_id) {
    const v = r.people_company_search_list_id.split(
      COMPANY_ID_FILTER_SEPARATOR
    )[0];
    req = addSearchListInclusionQuery(
      req,
      'searchList',
      'any',
      'include',
      v,
      latestActiveCompanyOnly ? QueryTarget.contact : QueryTarget.account,
      latestActiveCompanyOnly ? 'personLatestCompanyId' : 'companyId'
    );
  }

  if (r.include_person_id) {
    const cleanValue = r.include_person_id
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => i.split('::')[1])
      .join(FILTER_VALUE_SEPARATOR);
    req = addInclusionQuery(
      req,
      'id',
      'any',
      'include',
      cleanValue,
      QueryTarget.contact
    );
  }

  if (r.exclude_person_id) {
    const cleanValue = r.exclude_person_id
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => i.split('::')[1])
      .join(FILTER_VALUE_SEPARATOR);
    req = addInclusionQuery(
      req,
      'id',
      'any',
      'exclude',
      cleanValue,
      QueryTarget.contact
    );
  }

  // Bulk Input Linkedin
  if (r.bulk_include_person_linkedin_url) {
    const v = r.bulk_include_person_linkedin_url.split(
      BULK_FILTER_VALUE_SEPARATOR
    )[0];
    req = addSearchListInclusionQuery(
      req,
      'searchList',
      'any',
      'include',
      v,
      QueryTarget.contact,
      'personLinkedin'
    );
  }

  if (r.bulk_exclude_person_linkedin_url) {
    const v = r.bulk_exclude_person_linkedin_url.split(
      BULK_FILTER_VALUE_SEPARATOR
    )[0];
    req = addSearchListInclusionQuery(
      req,
      'searchList',
      'any',
      'exclude',
      v,
      QueryTarget.contact,
      'personLinkedin'
    );
  }

  if (r.include_linkedin_url_for_contact) {
    const v = r.include_linkedin_url_for_contact
      .trim()
      .split(FILTER_VALUE_SEPARATOR)
      .map((d: string) => cleanUrl(d))
      .join(FILTER_VALUE_SEPARATOR);
    req = addInclusionQuery(
      req,
      'linkedin',
      'any',
      'include',
      v,
      QueryTarget.contact
    );
  }

  return req;
}
